import './TransactionHistoryFilters.scss';

import { arrayOf, object } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import {
  selectHistoricalTransactionDateOptions,
  selectHistoricalTransactionTimePeriod,
  selectHistoricalTransactionIncludedAccounts,
  selectHistoricalTransactionTransactionType,
  TIME_PERIOD
} from 'state/transaction/TransactionActions';

import { initialTransactionsHistoryFilteringState } from 'state/transaction/TransactionReducers';

import {
  getTransactionsHistoryFilteringSelections,
  getFilteringSelections,
  getRawHistoryTransactions
} from 'state/transaction/TransactionSelectors';
import {
  furthestDateFromReferenceDate,
  isValidInputDate,
  yyyymmdd
} from 'services/Dates';

import Store from 'state/Store';

import TimePeriodSelect from 'components/shared/PeriodSelector/TimePeriodSelect';

import LabeledSelect from 'components/shared/SelectOption/LabeledSelect';
import DateRangeInputBeacon from 'components/shared/Input/DateRangeInputBeacon';
import { activityPageBeaconDatePickerEnabled } from 'services/FeatureFlags';
import DateRangeInput from 'components/shared/Input/DateRangeInput';

const selectDatePeriod = (state) => {
  const { fromDate, toDate } = state;
  if (isValidInputDate(fromDate) && isValidInputDate(toDate)) {
    Store.dispatch(
      selectHistoricalTransactionDateOptions({
        fromDate,
        toDate
      })
    );
  } else if (!isValidInputDate(fromDate) && !isValidInputDate(toDate)) {
    const value =
      initialTransactionsHistoryFilteringState[TIME_PERIOD].selection;
    Store.dispatch(selectHistoricalTransactionTimePeriod(value));
  }
};

const selectDatePeriodBeacon = (fromDate, toDate) => {
  if (isValidInputDate(fromDate) && isValidInputDate(toDate)) {
    Store.dispatch(
      selectHistoricalTransactionDateOptions({
        fromDate,
        toDate
      })
    );
  } else if (!isValidInputDate(fromDate) && !isValidInputDate(toDate)) {
    const value =
      initialTransactionsHistoryFilteringState[TIME_PERIOD].selection;
    Store.dispatch(selectHistoricalTransactionTimePeriod(value));
  }
};

const selectTimePeriod = (event) => {
  const value = event
    ? event.target.value
    : initialTransactionsHistoryFilteringState[TIME_PERIOD].selection;

  return Store.dispatch(selectHistoricalTransactionTimePeriod(value));
};

const selectIncludedAccounts = (event) =>
  Store.dispatch(
    selectHistoricalTransactionIncludedAccounts(event.target.value)
  );

const selectTransactionType = (event) =>
  Store.dispatch(
    selectHistoricalTransactionTransactionType(event.target.value)
  );

class TransactionHistoryFilters extends React.Component {
  state = {
    fromDate: '',
    showTimePeriodDropdown: true,
    toDate: ''
  };

  toggleTimePeriod = () => {
    this.setState(
      (prevState) => ({
        fromDate: '',
        showTimePeriodDropdown: !prevState.showTimePeriodDropdown,
        toDate: ''
      }),
      () => {
        if (this.state.showTimePeriodDropdown) {
          selectTimePeriod();
        }
      }
    );
  };

  toggleFromDate = (date) => {
    if (!activityPageBeaconDatePickerEnabled()) {
      this.setState(
        {
          fromDate: date
        },
        () => {
          selectDatePeriod(this.state);
        }
      );
    } else {
      this.setState({
        fromDate: date
      });
    }
  };

  toggleToDate = (date) => {
    if (!activityPageBeaconDatePickerEnabled()) {
      this.setState(
        {
          toDate: date
        },
        () => {
          selectDatePeriod(this.state);
        }
      );
    } else {
      this.setState({
        toDate: date
      });
    }
  };

  render() {
    const {
      filteringSelections,
      rawTransactionsHistory,
      transactionHistoryFilteringSelections
    } = this.props;
    const { fromDate, showTimePeriodDropdown, toDate } = this.state;
    const transactionTypeOptions =
      transactionHistoryFilteringSelections.transactionType.options.map(
        (curr) => {
          return {
            label:
              curr.label.toUpperCase() === 'ALL TRANSACTION TYPES'
                ? curr.label
                : curr.label.toUpperCase(),
            value: curr.value
          };
        }
      );
    const minDate = furthestDateFromReferenceDate(rawTransactionsHistory);
    const maxDate = yyyymmdd();

    return (
      <section className="pure-u-1 historical-transaction-filters">
        <div className="historical-transaction-filters-preamble">
          <p className="historical-transaction-filters-emphasized">
            Filter by any of the following options. Transaction data below will
            be updated automatically.
          </p>
          <p>
            You can view your transaction history up to 3 years, or the last
            1,200 records.
          </p>
        </div>
        <div className="historical-transaction-filters-group">
          {showTimePeriodDropdown ? (
            <TimePeriodSelect
              ariaLabel="custom-time-range"
              onChange={selectTimePeriod}
              options={transactionHistoryFilteringSelections.timePeriod.options}
              selection={
                transactionHistoryFilteringSelections.timePeriod.selection
              }
              toggleTimePeriod={this.toggleTimePeriod}
              rangeLabel="View Custom Range"
            />
          ) : activityPageBeaconDatePickerEnabled() ? (
            <DateRangeInputBeacon
              fromDate={fromDate}
              maxDate={maxDate}
              minDate={minDate}
              toDate={toDate}
              hideLabels={true}
              fromLabel={'From'}
              toLabel={'To'}
              goCTAClickFunction={selectDatePeriodBeacon}
              toggleFromDate={this.toggleFromDate}
              toggleTimePeriod={this.toggleTimePeriod}
              toggleToDate={this.toggleToDate}
              timePeriodLabel="View Time Period"
              customClasses={'beacon-date-picker'}
            />
          ) : (
            <DateRangeInput
              fromDate={fromDate}
              maxDate={maxDate}
              minDate={minDate}
              toDate={toDate}
              toggleFromDate={this.toggleFromDate}
              toggleTimePeriod={this.toggleTimePeriod}
              toggleToDate={this.toggleToDate}
              timePeriodLabel="View Time Period"
            />
          )}
          <div className="select-group">
            <LabeledSelect
              label="Included Accounts"
              ariaLabel={'included-accounts'}
              onChange={selectIncludedAccounts}
              options={filteringSelections.options}
              selection={
                transactionHistoryFilteringSelections.includedAccounts.selection
              }
            />
          </div>
          <div className="select-group">
            <LabeledSelect
              label="Transaction Type"
              className="select-group"
              ariaLabel={'transaction-type'}
              onChange={selectTransactionType}
              options={transactionTypeOptions}
              selection={
                transactionHistoryFilteringSelections.transactionType.selection
              }
            />
          </div>
        </div>
      </section>
    );
  }
}

TransactionHistoryFilters.propTypes = {
  filteringSelections: object,
  rawTransactionsHistory: arrayOf(object)
};

const mapStateToProps = (state) => ({
  transactionHistoryFilteringSelections:
    getTransactionsHistoryFilteringSelections(state),
  rawTransactionsHistory: getRawHistoryTransactions(state),
  filteringSelections: getFilteringSelections(state)
});

export default withRouter(connect(mapStateToProps)(TransactionHistoryFilters));
